import {
  stripTokenFromFreeText,
  stripWaitlistUnsubscribeTokenFromUrl,
} from './sanitizeAnalyticsUrl';

type SentryBreadcrumb = {
  message?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

type SentryEvent = {
  event_id?: string;
  timestamp?: number | string;
  level?: string;
  sdk?: unknown;
  request?: {
    url?: string;
    query_string?: string;
    headers?: Record<string, string>;
  };
  breadcrumbs?: SentryBreadcrumb[];
  contexts?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
  transaction?: string;
  message?: string;
  [key: string]: unknown;
};

const MAX_SCRUB_DEPTH = 20;

/** Strip token query params including percent-encoded key/value forms. */
export function scrubString(value: string): string {
  let out = value;
  try {
    const decoded = decodeURIComponent(out);
    if (decoded !== out) {
      out = decoded;
    }
  } catch {
    /* keep out */
  }
  out = out
    .replace(/([?&])(?:token|t%6[fF]ken|%74oken)(?:=|%3[dD])[^&\s"'#]*/gi, '$1')
    .replace(/%3[fF](?:token|t%6[fF]ken|%74oken)%3[dD][^&#'"\s]*/gi, '?')
    .replace(/%26(?:token|t%6[fF]ken|%74oken)%3[dD][^&#'"\s]*/gi, '&');
  out = stripWaitlistUnsubscribeTokenFromUrl(out);
  out = stripTokenFromFreeText(out);
  if (out.includes('waitlist/unsubscribe') && /token(?:=|%3[dD])/i.test(out)) {
    out = out
      .replace(/([?&])(?:token|t%6[fF]ken|%74oken)(?:=|%3[dD])[^&\s"'#]*/gi, '$1')
      .replace(/\?&/, '?')
      .replace(/[?&]$/, '');
  }
  if (!out.includes('waitlist/unsubscribe') && /(?:^|&)token(?:=|%3[dD])/i.test(out)) {
    out = out
      .replace(/(?:^|&)(?:token|t%6[fF]ken|%74oken)(?:=|%3[dD])[^&]*/gi, '')
      .replace(/^&/, '')
      .replace(/&&+/g, '&');
  }
  return out;
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function scrubUnknownDeep(
  value: unknown,
  visited: WeakSet<object>,
  depth: number,
): unknown {
  if (depth > MAX_SCRUB_DEPTH) {
    return '[MaxDepth]';
  }
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? '' : value.toISOString();
  }
  if (value instanceof Error) {
    const head = `${value.name}: ${value.message}`;
    const stack = value.stack ? scrubString(value.stack) : '';
    return stack ? scrubString(`${head}\n${stack}`) : scrubString(head);
  }
  if (Array.isArray(value)) {
    if (visited.has(value)) {
      return '[Circular]';
    }
    visited.add(value);
    try {
      return value.map((item) => scrubUnknownDeep(item, visited, depth + 1));
    } finally {
      visited.delete(value);
    }
  }
  if (value && typeof value === 'object') {
    if (!isPlainObject(value)) {
      return scrubString(String(value));
    }
    if (visited.has(value)) {
      return '[Circular]';
    }
    visited.add(value);
    try {
      const out: Record<string, unknown> = {};
      for (const [key, nested] of Object.entries(value)) {
        out[key] = scrubUnknownDeep(nested, visited, depth + 1);
      }
      return out;
    } finally {
      visited.delete(value);
    }
  }
  return value;
}

export function scrubUnknown(value: unknown): unknown {
  return scrubUnknownDeep(value, new WeakSet(), 0);
}

function minimalSafeEventFrom(event: SentryEvent): SentryEvent {
  return {
    event_id: event.event_id,
    timestamp: event.timestamp,
    level: event.level,
    sdk: event.sdk,
    message: 'scrub_failed',
  };
}

function replaceEventContents<E extends SentryEvent>(
  event: E,
  next: Record<string, unknown>,
): E {
  for (const key of Object.keys(event as object)) {
    try {
      delete (event as Record<string, unknown>)[key];
    } catch {
      (event as Record<string, unknown>)[key] = undefined;
    }
  }
  Object.assign(event, next);
  return event;
}

export function scrubSentryEvent<E extends SentryEvent>(event: E): E {
  try {
    const scrubbed = scrubUnknown(event) as Record<string, unknown>;
    return replaceEventContents(event, scrubbed);
  } catch {
    return minimalSafeEventFrom(event) as E;
  }
}

export function scrubSentryBreadcrumb<B extends SentryBreadcrumb>(breadcrumb: B): B {
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubString(breadcrumb.message);
  }
  if (breadcrumb.data) {
    breadcrumb.data = scrubUnknown(breadcrumb.data) as Record<string, unknown>;
  }
  return breadcrumb;
}
